import {Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleXmark,
  faClipboardCheck
} from "@fortawesome/pro-solid-svg-icons";
import {ActivatedRoute} from "@angular/router";
import {ClassificationRule, HOAICheck, HoaiCheckControllerService, HOAIRule, ProjectFile, Task} from "../../../../api";
import {Answer} from "../../../../api/model/answer";
import {CheckFileRule} from "../../../../api/model/checkFileRule";
import {StateConfigService} from "../../../../services/state/state-config.service";
import {OverviewReportComponent} from "../../overviewReport/overview-report.component";
import {HoaiStepperComponent} from "../hoai-stepper/hoai-stepper.component";
import {LphToNumberPipe} from "../../../../pipes/lphToNumber.pipe";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicResultColumnComponent,
  BicResultComponent,
  BicResultHeaderComponent,
  BicResultRowComponent
} from "@buildinginformationcloud/bic-ui-components";
import {SortHoaiRulesPipe} from "../../../../pipes/sort-hoai-rules.pipe";
import {TaskDetailsService} from "../../../../services/tasks/task-details.service";
import {FilesComponent} from "../../../files/files.component";
import {combineLatest} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {HttpClient} from "@angular/common/http";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";

type ExtendedTypeEnum = Answer.TypeEnum | 'CLASSIFICATION';
type ExtendedAnswer = Answer & {
  type?: ExtendedTypeEnum;
};

/**
 * HOAI-spezifische CheckResult-Struktur.
 * Weicht vom Workflow-CheckResult (API-Model) ab: enthält `files` und `rules`.
 */
interface HoaiCheckResult {
  files?: Array<{ gl?: CheckFileRule[]; bl?: CheckFileRule[] }>;
  rules?: string[];
}

@Component({
  selector: 'app-hoai-check-reports',
  templateUrl: './hoai-check-report.component.html',
  styleUrl: './hoai-check-report.component.scss',

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    OverviewReportComponent,
    HoaiStepperComponent,
    LphToNumberPipe,
    FaIconComponent,
    BicResultColumnComponent,
    BicResultComponent,
    BicResultHeaderComponent,
    BicResultRowComponent,
    SortHoaiRulesPipe,
    BicBadgeComponent,
    BicButtonComponent,
    FilesComponent
  ]
})
export class HoaiCheckReport implements OnInit {
  protected stateConfigService = inject(StateConfigService);
  private activatedRoute = inject(ActivatedRoute);
  private hoaiCheckControllerService = inject(HoaiCheckControllerService);
  private destroyRef = inject(DestroyRef);
  private httpClient = inject(HttpClient);

  private get apiBasePath(): string {
    return (this.hoaiCheckControllerService as any).basePath ?? 'http://localhost:8080';
  }

  classifications: ClassificationRule[] = [];


  rules: HOAIRule[] = [];
  customRuleSetNames = new Map<string, string>();
  hoaiCheck: HOAICheck = {};

  get projectFilesForSelectedRule(): ProjectFile[] {
    const relevantCheckFileRules = this.hoaiCheckResult?.files?.flatMap(f => [...(f.gl ?? []), ...(f.bl ?? [])])
      .filter(c => c.rule_id!.toUpperCase() === this.selectedRule?.id?.toUpperCase()!) ?? [];

    const fileIdsWithSuccessfulIdentification = relevantCheckFileRules
      .filter(c => !c.failed)
      .filter(c => !this.identificationFailedForCheckFileRule(c))
      .map(c => c.file_id!.toUpperCase());

    return this.hoaiCheck.projectFiles?.filter(pf =>
      fileIdsWithSuccessfulIdentification.includes(pf.id!.toUpperCase())
    ) ?? [];
  }

  loading = true;
  selectedStep = 1;

  selectedRule: HOAIRule | undefined = undefined;
  selectedFile: ProjectFile | undefined = undefined;
  protected readonly faChevronRight = faChevronRight;

  /**
   * Ob die (mittlere) Dateien-Spalte ausgeblendet ist – deckungsgleich mit dem `[hide]`
   * in der Vorlage (ausgewählte Teilleistung ohne zugeordnete Dateien).
   */
  get filesColumnHidden(): boolean {
    return !!this.selectedRule && this.projectFilesForSelectedRule.length === 0;
  }

  /**
   * Spaltenbreiten (flex-grow-Anteil): Ergebnis-Spalte betont mit 50%, die übrigen
   * sichtbaren Spalten teilen sich die restlichen 50% gleichmäßig
   * (3 Spalten -> 25/25/50, bei ausgeblendeter Dateien-Spalte 50/50).
   * `files` ist null, wenn die Spalte ausgeblendet ist (dann greift das `[hide]`-Styling).
   */
  get columnWidths(): { rules: number; files: number | null; detail: number } {
    return this.filesColumnHidden
      ? {rules: 50, files: null, detail: 50}
      : {rules: 25, files: 25, detail: 50};
  }
  private taskDetailService = inject(TaskDetailsService);

  protected readonly faClipboardCheck = faClipboardCheck;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;

  get total(): number {
    return this.rules.length;
  }

  protected readonly faCircleXmark = faCircleXmark;

  get completed(): number {
    const res = this.rules.map(r => this.statusForRuleResult(r));
    const completedRules = res.filter(r => r == 'success');

    if (completedRules.length > 0) {
      return (completedRules.length / this.total) * 100;
    }
    return 0;
  }

  get warning(): number {
    const res = this.rules.map(r => this.statusForRuleResult(r));
    const warningRules = res.filter(r => r == 'warning');

    if (warningRules.length > 0) {
      return (warningRules.length / this.total) * 100;
    }
    return 0;
  }

  get error(): number {
    const res = this.rules.map(r => this.statusForRuleResult(r));
    const errorRules = res.filter(r => r == 'error');

    if (errorRules.length > 0) {
      return (errorRules.length / this.total) * 100;
    }
    return 0;
  }

  get lph() {
    return this.rules && this.rules.length > 0 ? this.rules[0].servicePhase! : undefined;
  }

  get resultOfSelectedProjectFileAndSelectedRule(): ExtendedAnswer[] {
    const answers: ExtendedAnswer[] = [
      ...this.hoaiCheckResult?.files?.flatMap(cr => cr.gl!) || [],
      ...this.hoaiCheckResult?.files?.flatMap(cr => cr.bl!) || []
    ].filter(c => c.file_id!.toUpperCase() === this.selectedFile?.id?.toUpperCase())
      .filter(c => !c.failed)
      .filter(c => !this.identificationFailedForCheckFileRule(c))
      .filter(c => c.rule_id!.toUpperCase() === this.selectedRule?.id?.toUpperCase())
      .flatMap(c => c.result!)
      .filter(a => a.type !== "SCOPE2" && a.type !== "VALIDATION") ?? [];

    // Check IDENTIFICATION result
    const identificationResult = answers.find(a => a.type === Answer.TypeEnum.IDENTIFICATION);
    const hasIdentificationIssue = identificationResult?.result === Answer.ResultEnum.WARNING ||
      identificationResult?.result === Answer.ResultEnum.FAILURE;

    // Check SCOPE1 result
    const scope1Result = answers.find(a => a.type === Answer.TypeEnum.SCOPE1);
    const hasScope1Issue = scope1Result?.result === Answer.ResultEnum.WARNING ||
      scope1Result?.result === Answer.ResultEnum.FAILURE;

    // Filter answers based on conditions
    const filteredAnswers = answers.filter(a => {
      // Filter out SCOPE1 if IDENTIFICATION has warning or error
      if ((a.type === Answer.TypeEnum.SCOPE1 || a.type === Answer.TypeEnum.CONTENT) && hasIdentificationIssue) {
        return false;
      }
      // Filter out CONTENT if SCOPE1 has warning or error
      if (a.type === Answer.TypeEnum.CONTENT && hasScope1Issue) {
        return false;
      }
      return true;
    });

    return [{
      type: "CLASSIFICATION",
      answer: this.selectedFile?.classificationRules?.map(c => c.name).join(", "),
      result: "SUCCESS"
    }, ...filteredAnswers] as ExtendedAnswer[]
  }

  /** Typsicherer Zugriff auf das HOAI-spezifische CheckResult (Runtime-Struktur weicht vom API-Model ab). */
  private get hoaiCheckResult(): HoaiCheckResult | undefined {
    return this.hoaiCheck?.checkResult as unknown as HoaiCheckResult | undefined;
  }

  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faSpinnerThird = faSpinnerThird;

  identificationFailedForCheckFileRule(checkFileRule: CheckFileRule): boolean {
    return checkFileRule.result?.some(r => r!.type! == Answer.TypeEnum.IDENTIFICATION && (r!.result! == Answer.ResultEnum.WARNING || r!.result! == Answer.ResultEnum.FAILURE)) ?? false;
  }

  statusForRuleResult(checkRule: HOAIRule) {
    const gl = this.hoaiCheckResult?.files?.flatMap(f => f.gl!)
    const bl = this.hoaiCheckResult?.files?.flatMap(f => f.bl!)
    const allRules = [...(gl || []), ...(bl || [])].filter(r => r.rule_id!.toUpperCase() === checkRule!.id!.toUpperCase())
      .filter(c => !c.failed)
      .filter(c => !this.identificationFailedForCheckFileRule(c))

    if (allRules.length == 0) {
      return 'error'
    }

    // Compute per-file status and return the best result across all files
    const fileIds = [...new Set(allRules.filter(r => r.file_id).map(r => r.file_id!.toUpperCase()))];
    const fileStatuses = fileIds.map(fileId => {
      const fileRules = allRules.filter(r => r.file_id?.toUpperCase() === fileId);
      if (fileRules.some(r => r.result?.some(a => a.result == Answer.ResultEnum.FAILURE))) {
        return 'error';
      } else if (fileRules.some(r => r.result?.some(a => a.result == Answer.ResultEnum.WARNING))) {
        return 'warning';
      } else {
        return 'success';
      }
    });

    if (fileStatuses.some(s => s === 'success')) return 'success';
    if (fileStatuses.some(s => s === 'warning')) return 'warning';
    return 'error';
  }

  private mapResultToStatus(result?: Answer.ResultEnum): 'error' | 'warning' | 'success' {
    if (result === Answer.ResultEnum.FAILURE) {
      return 'error';
    } else if (result === Answer.ResultEnum.WARNING) {
      return 'warning';
    } else {
      return 'success';
    }
  }

  statusForProjectFile(projectFile: ProjectFile) {
    const gl = this.hoaiCheckResult?.files?.flatMap(f => f.gl!)
    const bl = this.hoaiCheckResult?.files?.flatMap(f => f.bl!)
    const allRules = [...(gl || []), ...(bl || [])].filter(r => r.file_id!.toUpperCase() === projectFile.id!.toUpperCase())
      .filter(r => !this.selectedRule || r.rule_id!.toUpperCase() === this.selectedRule.id!.toUpperCase())
      .filter(c => !c.failed)
      .filter(c => !this.identificationFailedForCheckFileRule(c))

    if (allRules.length == 0) {
      return 'error';
    }

    // Get all answers for this file (scoped to the selected rule)
    const allAnswers = allRules.flatMap(r => r.result ?? [])
      .filter(a => a.type !== "SCOPE2" && a.type !== "VALIDATION");

    if (allAnswers.length === 0) {
      return 'success';
    }

    // Workflow order is always: IDENTIFICATION -> SCOPE1 -> CONTENT
    // Return the status of the last step that was reached in this workflow chain

    // Step 1: Check IDENTIFICATION result
    const identificationResult = allAnswers.find(a => a.type === Answer.TypeEnum.IDENTIFICATION);
    if (identificationResult) {
      const status = this.mapResultToStatus(identificationResult.result);
      if (status === 'error' || status === 'warning') {
        return status;
      }
    }

    // Step 2: Check SCOPE1 result (only reached if IDENTIFICATION was successful)
    const scope1Result = allAnswers.find(a => a.type === Answer.TypeEnum.SCOPE1);
    if (scope1Result) {
      const status = this.mapResultToStatus(scope1Result.result);
      if (status === 'error' || status === 'warning') {
        return status;
      }
    }

    // Step 3: Check CONTENT result (only reached if IDENTIFICATION and SCOPE1 were successful)
    const contentResult = allAnswers.find(a => a.type === Answer.TypeEnum.CONTENT);
    if (contentResult) {
      return this.mapResultToStatus(contentResult.result);
    }

    // Fallback: If no specific result types found, return success
    return 'success';
  }

  ngOnInit(): void {
    const hoaiCheckId = this.activatedRoute.snapshot.paramMap.get('checkId')

    this.hoaiCheckControllerService.getClassificationRules().subscribe(rules => {
      this.classifications = rules;
    })


    combineLatest([
      this.hoaiCheckControllerService.getHOAICheck(hoaiCheckId!, this.stateConfigService.getProjectId()),
      this.hoaiCheckControllerService.getHoaiRules(),
      this.hoaiCheckControllerService.getCustomRules(this.stateConfigService.getProjectId())
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([check, rules, customRules]) => {

        this.hoaiCheck = check;

        // If checkResult or rules don't exist, show all rules
        const ruleIdsToInclude = this.hoaiCheckResult?.rules?.map(r => r.toUpperCase()) || [];

        if (ruleIdsToInclude.length === 0) {
          // If no specific rules in checkResult, include all rules from API
          this.rules = rules || [];
        } else {
          // Filter to only rules mentioned in checkResult
          this.rules = (rules || []).filter(r => ruleIdsToInclude.includes(r.id!.toUpperCase()));
        }

        const customRulesForCheck = (customRules || []).flatMap(r => {
          if (r && 'rules' in r && r.rules) {
            if (ruleIdsToInclude.length === 0) {
              // No specific rules, include all custom rules
              return r.rules as HOAIRule[];
            } else {
              // Filter custom rules
              const setRules = (r.rules as HOAIRule[]).filter(rule => ruleIdsToInclude.includes(rule.id!.toUpperCase()));
              setRules.forEach(rule => this.customRuleSetNames.set(rule.id!.toUpperCase(), r.readableName ?? 'CUSTOM'));
              return setRules;
            }
          }
          return [];
        });

        this.rules = [...this.rules, ...customRulesForCheck];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading HOAI check data:', err);
      }
    });
  }



  createTaskForRule(rule: HOAIRule) {
    let task: Task = {
      lph: rule.servicePhase,
      title: 'Überarbeiten notwendig bezüglich Regel: ' + rule.name,
    }

    this.taskDetailService.setOpenedTask(task as Task);
  }

  createTaskForRuleAndSelectedFile(file: ProjectFile) {
    const ruleName = this.selectedRule?.name ?? '';
    let task: Task = {
      lph: this.selectedRule?.servicePhase,
      title: 'Überarbeiten notwendig für: ' + file.name + ' bezüglich Regel: ' + ruleName,
    }

    this.taskDetailService.setOpenedTask(task as Task);
  }

  protected downloadPdf() {
    this.httpClient.get(`${this.apiBasePath}/hoai/${encodeURIComponent(this.hoaiCheck.id!)}/report`, {
      params: {projectId: this.stateConfigService.getProjectId().toString()},
      responseType: 'blob'
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bericht_hoai_check.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  }

  classificationNameForId(classificationId: string): string {
    const name = this.classifications?.find(c => c.id?.toLowerCase() === classificationId.toLowerCase())?.name;

    return name ?? classificationId;
  }

  getRuleTypeLabel(rule: HOAIRule): string {
    if (rule.serviceType === HOAIRule.ServiceTypeEnum.CUSTOM) {
      return this.customRuleSetNames.get(rule.id!.toUpperCase()) ?? rule.serviceType;
    }
    return rule.serviceType !== HOAIRule.ServiceTypeEnum.OTHER ? rule.serviceType! : 'AN';
  }
}


